import { DatePipe } from '@angular/common';
import { Reservation } from '../../model/reservation/reservation.model';
import { dateDifference, useDefault } from '../../../common/common';
import { GuestAddress } from '../../model/guest/guest-address.model';
import { Guest } from '../../model/guest/guest.model';
import { Stay } from '../../model/reservation/stay.model';
import { Payment } from '../../model/reservation/payment.model';
import { DateUtils } from '../../../common/date.utils';
import { Room } from '../../model/reservation/room/room.model';
import { Rate } from '../../model/reservation/rate/rate.model';
import { RateCharge } from '../../model/reservation/rate/rate-charge.model';
import { ChargeType } from '../../model/reservation/rate/charge.type.model';
import { RateChange } from '../../model/reservation/rate/rate-change.model';
import { CancellationPolicy } from '../../model/reservation/rate/cancellation-policy.model';
import { RateCharges } from '../../model/reservation/rate/rate-charges.model';
import { RateRule } from '../../model/reservation/rate/rate-rule.model';
import { RateRuleType } from '../../model/reservation/rate/rate-rule.type.model';
import { DepositPolicy } from '../../model/reservation/rate/deposit-policy.model';
import { DepositPolicyType } from '../../model/reservation/rate/deposit-policy.type.model';
import { SegmentReferenceIds } from '../../model/reservation/segmentReferenceIds.model';
import { CommunicationPreferences } from '../../model/communication/communication.preferences.model';
import { AdditionalGuestRestriction } from '../../model/reservation/rate';
import * as moment from 'moment';

const ZERO = 0;
const SERVICECODES = ['FTT14'];
const TAXCODES = ['FTT17', 'FTT03', 'FTT3'];
const SLEEPING_ROOM = 'SR';

export class ReservationV2ModelBuilder {
  private _model: Reservation;
  private _datePipe: DatePipe;
  constructor() {
    this._model = new Reservation();
    this._datePipe = new DatePipe('en-US');
  }

  withReservationData(reservationData: any) {
    const hotelReservation = reservationData && reservationData.hotelReservation ? reservationData.hotelReservation : {};
    const reservationIds = hotelReservation && hotelReservation.reservationIds ? hotelReservation.reservationIds : {};
    const rateCategory = hotelReservation && hotelReservation.rateCategory ? hotelReservation.rateCategory : {};
    const hotel = hotelReservation && hotelReservation.hotels && hotelReservation.hotels.length && hotelReservation.hotels[0] && hotelReservation.hotels[0] ? hotelReservation.hotels[0] : {};
    const ratePlanDefinition = hotelReservation && hotelReservation.ratePlanDefinitions && hotelReservation.ratePlanDefinitions.length && hotelReservation.ratePlanDefinitions[0] ? hotelReservation.ratePlanDefinitions[0] : {};
    const userProfiles = hotelReservation && hotelReservation.userProfiles && hotelReservation.userProfiles.length && hotelReservation.userProfiles ? hotelReservation.userProfiles : [];
    const guest = userProfiles && userProfiles.length && userProfiles[0] ? userProfiles[0] : {};
    const segment = hotelReservation && hotelReservation.segments && hotelReservation.segments.length && hotelReservation.segments[0] && hotelReservation.segments[0] ? hotelReservation.segments[0] : {};
    const offer = segment && segment.offer ? segment.offer : {};
    const payment = segment && segment.payment ? segment.payment : {};
    const totalRate = offer && offer.rates && offer.rates.totalRate ? offer.rates.totalRate : {};
    const corporateAccountInfo = !!segment && Array.isArray(segment.corporateAccountInfo) ? segment.corporateAccountInfo[0] || {} : {};
    const communicationPreferences = !!segment && segment.communicationPreferences ? segment.communicationPreferences : {};
    let productDefinition = { productCode: '' };
    let productUse = {};
    if (hotelReservation && hotelReservation.productDefinitions && hotelReservation.productDefinitions.length) {
      productDefinition = this.getSpecificProductType(hotelReservation.productDefinitions, SLEEPING_ROOM);
      if (offer && offer.productUses && offer.productUses.length) {
        productUse = this.getSpecificProduct(offer.productUses, productDefinition.productCode);
      }
    }
    this._model.confirmationNumber = reservationIds.confirmationNumbers && reservationIds.confirmationNumbers.length ? reservationIds.confirmationNumbers[0].ihgConfirmationNumber : null
    this._model.cancellationNumber = useDefault(reservationIds.cancellationNumber, null);
    this._model.status = useDefault(hotelReservation.reservationStatus, '');
    this._model.originalSellDate = DateUtils.toShortISODateFromString(hotelReservation.createDateTime);
    this._model.hotelMnemonic = useDefault(hotel.hotelMnemonic, '');
    this._model.room.code = useDefault(productDefinition.productCode, '');
    this._model.rate.code = useDefault(ratePlanDefinition.ratePlanCode, '');
    this._model.corporateId = useDefault(corporateAccountInfo.corporateNumber, '');
    this._model.stay = this._buildStay(productUse, totalRate);
    this._model.guest = this._buildGuest(guest);
    this._model.payment = this._buildPayment(payment);
    this._model.additionalGuests = this._buildAdditionalGuests(userProfiles);
    this._model.room = this._buildRoom(productDefinition, hotel);
    this._model.rate = this._buildRate(ratePlanDefinition, rateCategory, hotel, segment);
    this._model.segmentReferenceIds = this._buildIds(segment);
    this._model.offerCode = this._buildOfferCode(ratePlanDefinition, segment);
    this._model.communicationPreferences = this._buildCommunicationPreferences(communicationPreferences, guest);

    return this;
  }

  build(): Reservation {
    return this._model;
  }

  private _buildPayment(paymentData) {
    const payment: Payment = new Payment();
    payment.guaranteeType = useDefault(paymentData.guaranteeType, null);
    if (paymentData.paymentInformation) {
      payment.deposit = useDefault(paymentData.paymentInformation.depositAmount, null);
      if (paymentData.paymentInformation.card) {
        payment.creditCard = {};
        payment.creditCard.type = useDefault(paymentData.paymentInformation.card.code, null);
        payment.creditCard.name = useDefault(paymentData.paymentInformation.card.name, null);
        payment.creditCard.token = useDefault(paymentData.paymentInformation.card.number, null);
        payment.creditCard.cvv = useDefault(paymentData.paymentInformation.card.cvv, null);
        payment.creditCard.expirationDate = useDefault(paymentData.paymentInformation.card.expireDate, null);
      }
    }
    return payment;
  }

  private _buildStay(productUse: any, rate: any): Stay {
    const stay: Stay = new Stay();
    const period = productUse && productUse.periods && productUse.periods.length && productUse.periods[0] ? productUse.periods[0] : {};

    stay.numberOfChildren = useDefault(productUse.children, 0);
    stay.numberOfAdults = useDefault(productUse.adults, 0);
    stay.numberOfRooms = useDefault(productUse.quantity, 0);
    stay.checkInDate = DateUtils.fromISOToDate(period.start);
    stay.checkOutDate = DateUtils.fromISOToDate(period.end);
    stay.numberOfNights = dateDifference(period.start, period.end);
    stay.totalPrice = useDefault(rate.amountAfterTax, null);
    return stay;
  }

  private _buildAdditionalGuests(additionalGuestsData): Array<Guest> {// operating on the assumption that every userProfile after the first is an additional guest. Will need verified during integration.
    const additionalGuests: Array<Guest> = new Array<Guest>();
    for (let i = 1; i < additionalGuestsData.length; i++) {
      additionalGuests.push(this._buildGuest(useDefault(additionalGuestsData[i], {})));
    }
    return additionalGuests;
  }

  private _buildGuest(guestData): Guest {
    const name = guestData && guestData.personName ? guestData.personName : {};
    const address = guestData && guestData.addresses && guestData.addresses.length && guestData.addresses[0] ? guestData.addresses[0] : {};
    const loyaltyId = guestData && guestData.loyaltyProgram ? guestData.loyaltyProgram.loyaltyId : '';
    const id = guestData && guestData.id ? guestData.id : -1;
    const guest: Guest = new Guest(useDefault(name.given, ''), useDefault(name.surname, ''), useDefault(loyaltyId, ''), useDefault(id, -1));
    guest.phoneNumber = useDefault('', ''); // not in response.
    guest.address = this._buildGuestAddress(useDefault(address, {}));
    return guest;
  }

  private _buildGuestAddress(guestAddressData): GuestAddress {
    const addressLines = guestAddressData.addressLines ? guestAddressData.addressLines : [];
    const address: GuestAddress = new GuestAddress();
    address.countryCode = useDefault(guestAddressData.countryCode, '');
    address.stateCode = useDefault(guestAddressData.stateProvince, '');
    address.postalCode = useDefault(guestAddressData.postalCode, '');
    address.city = useDefault(guestAddressData.cityName, '');
    address.addressLine1 = useDefault(addressLines[0], '');
    address.addressLine2 = useDefault(addressLines[1], '');
    address.addressLine3 = useDefault(addressLines[2], '');
    return address;
  }

  private _buildRoom(prodDef, hotel): Room {
    const room: Room = new Room();
    room.code = useDefault(prodDef.productCode, '');
    room.description = useDefault(prodDef.productName, '');
    room.maxNumberOfPeople = useDefault(prodDef.maximumNumberParticipant, 0);
    room.smoking = prodDef.attributeCodes && prodDef.attributeCodes.length ? prodDef.attributeCodes.indexOf('SMK') !== -1 : false;
    room.wheelchairAccessible = prodDef.attributeCodes && prodDef.attributeCodes.length ? prodDef.attributeCodes.indexOf('AR') !== -1 : false;
    room.currencyCode = useDefault(hotel.propertyCurrency, '');
    // room.numRoomsAvailable = useDefault(roomData.numRoomsAvailable, 0);  //not available. Is this even used?
    return room;
  }

  private _buildRate(rateData: any, rateCategory: any, hotel: any, segment: any): Rate {
    const offer = segment && segment.offer ? segment.offer : {};
    const payment = segment && segment.payment ? segment.payment : {};
    const policies = offer && offer.policies ? offer.policies : {};

    const rate: Rate = new Rate();
    rate.code = useDefault(rateData.ratePlanCode, '');
    rate.name = this._setRateName(rateData, rateCategory);
    rate.desc = this._setRateDescription(rateData, rateCategory);
    rate.currencyCode = useDefault(hotel.propertyCurrency, '');
    rate.totalPointsCost = payment && payment.paymentByPoints && payment.paymentByPoints.length && payment.paymentByPoints[0].totalPoints ? payment.paymentByPoints[0].totalPoints : null;
    rate.earlyDepartureFee = policies.earlyDeparture && policies.earlyDeparture.amount ? policies.earlyDeparture.amount : null;
    rate.checkInTime = this._formatTime(policies.checkinTime);
    rate.checkOutTime = this._formatTime(policies.checkoutTime);
    rate.isRewardNight = useDefault(rateData.isRewardNight, false);
    rate.freeNight = useDefault(rateData.isFreeNight, false);
    rate.groupCode = useDefault(offer.groupContractCode, null)
    rate.memberExclusive = useDefault(rateData.isLoyaltyIdRequired, false);
    rate.vatIsExcluded = !this._areSomeTaxesIncluded(offer);
    // if reservation service carries secondaryCancellation policies in the same place with the same name as avail service then this will work.
    this._setCancellationPolicy(rate, useDefault(policies, null), [useDefault(policies.secondaryCancellation, null)]);
    this._setCharges(rate, rateData, segment);
    this._setRules(rate, rateData, policies);
    this._setDepositPolicy(rate, policies);
    // if reservation service carries additionalGuestRestriction policies in the same place with the same name as avail service then this will work.
    if (rateData.additionalGuestRestriction) {
      this._setAdditionalGuestRestriction(rate, useDefault(rateData.additionalGuestRestriction.body, null), useDefault(rateData.additionalGuestRestriction.header, null));
    }
    return rate;
  }

  private _buildIds(segment: any): SegmentReferenceIds {
    const ids: SegmentReferenceIds = new SegmentReferenceIds();
    const segmentId = segment && segment.id ? segment.id : -1;
    const guestId = segment && segment.guestId ? segment.guestId : -1;
    ids.segmentId = segmentId;
    ids.guestId = guestId;
    return ids;
  }

  /* Offer Code is only needed for Free night booking */
  private _buildOfferCode(ratePlan: any, segment: any): string {
    let offerCode = null;
    const freeNightRate = ratePlan && ratePlan.isFreeNight ? ratePlan.isFreeNight : false;
    const payment = segment && segment.payment ? segment.payment : {};
    if (freeNightRate && payment) {
      offerCode = payment.paymentByPoints && payment.paymentByPoints.length && payment.paymentByPoints[0].transactionId ? payment.paymentByPoints[0].transactionId : null;
    }
    return offerCode;
  }

  private _buildCommunicationPreferences(preferences: any, guest: any): CommunicationPreferences {
    /* V2 Reservations REST API: As of 4/11/2019, the new IHG booking tool bypass apigee and goes straight to ESB.
    Due to this, communication preferences will not be properly saved and associated with its reservation when using the booking tool.
    When this occurs, we need to use the guest details that are returned in the user profile object.
    */
    const communicationPreferences: CommunicationPreferences = new CommunicationPreferences();
    const guestEmailAddress = guest && guest.emails && guest.emails.length && guest.emails[0] && guest.emails[0].address ? guest.emails[0].address : '';
    communicationPreferences.emailAddress = preferences && preferences.emailAddresses && preferences.emailAddresses[0] ? preferences.emailAddresses[0] : guestEmailAddress;
    communicationPreferences.sendEmail = preferences && preferences.sendEmail ? preferences.sendEmail : true;
    communicationPreferences.sendSMS = preferences && preferences.sendSMS ? preferences.sendSMS : false;
    communicationPreferences.smsNumber = preferences && preferences.smsNumbers && preferences.smsNumbers[0] ? preferences.smsNumbers[0] : '';
    communicationPreferences.faxNumber = preferences && preferences.faxNumbers && preferences.faxNumbers[0] ? preferences.faxNumbers[0] : '';

    return communicationPreferences;
  }

  private _formatTime(time) {
    if (time) {
      const d = new Date();
      time = time.split(':', 2);
      d.setHours(time[0], time[1], 0, 0);
      return this._datePipe.transform(d, 'h:mm a');
    } else {
      return '';
    }
  };

  private _setCancellationPolicy(rate: Rate, policies: any, secondaryCancellationPolicy: Array<string>) {
    let primaryCancellationPolicy = '';
    if (policies.cancellationNoShow) {
      primaryCancellationPolicy = policies.cancellationNoShow.formattedDescription;
    } else if (policies.cancellation) {
      primaryCancellationPolicy = policies.cancellation.formattedDescription;
    } else {
      primaryCancellationPolicy = '';
    }

    rate.cancellationPolicy = new CancellationPolicy(primaryCancellationPolicy, secondaryCancellationPolicy);

  }

  private _setAdditionalGuestRestriction(rate: Rate, body: string, header: string) {
    rate.additionalGuestRestriction = new AdditionalGuestRestriction(body, header);
  };

  private _getAverageNightlyRate(rate) {
    let price = null;
    if (rate.daily && rate.daily.length) {
      for (let i = 0; i < rate.daily.length; i++) {
        if (rate.daily[i].amountType === 'AVERAGE') {
          price = rate.daily[i].baseAmount;
        }
      }
    }
    return price;
  };

  private _setCharges(rate: Rate, rateData, segment) {
    const offer = segment && segment.offer ? segment.offer : {};
    const allRates = offer && offer.rates ? offer.rates : {};
    const productUse = offer && offer.productUses && offer.productUses.length && offer.productUses[0] ? offer.productUses[0] : {};
    const payment = segment && segment.payment ? segment.payment : {};
    const taxDefs = segment && segment.taxDefinitions ? segment.taxDefinitions : {};
    const taxGroups = allRates && allRates.totalRate && allRates.totalRate.taxGroups && allRates.totalRate.taxGroups.length ? allRates.totalRate.taxGroups : [];

    this._setRateCharges(rate, allRates, taxDefs);
    this._setRateChanges(rate, allRates);

    const totalRate = useDefault(allRates.totalRate, {});
    const totalBaseRate = useDefault(allRates.totalBaseOccupancyRate, {});

    let avgNightlyRate = this._getAverageNightlyRate(totalBaseRate);
    let total = parseFloat(totalRate.amountAfterTax);
    let price = parseFloat(totalBaseRate.baseAmount);
    let taxes = this._getApplicableFees(taxGroups, TAXCODES);
    const serviceCharges = this._getApplicableFees(taxGroups, SERVICECODES);
    const extraPersonCharge = allRates.totalExtraOccupancyRate && allRates.totalExtraOccupancyRate.baseAmount ? parseFloat(allRates.totalExtraOccupancyRate.baseAmount) : null;
    const extraPersonAndTaxes = extraPersonCharge + taxes;

    if (rateData.isRewardNight) {
      const hasPaymentByPoints = !!(payment && payment.paymentByPoints && payment.paymentByPoints.length);

      const dailyPointsCost = Number(hasPaymentByPoints && payment.paymentByPoints[0].dailyPoints ? payment.paymentByPoints[0].dailyPoints : 0);
      const totalPointsCost = Number(hasPaymentByPoints && payment.paymentByPoints[0].totalPoints ? payment.paymentByPoints[0].totalPoints : 0);
      avgNightlyRate = dailyPointsCost;
      price = totalPointsCost;
      total = totalPointsCost;

      rate.rateChanges = new Array<RateChange>();
      if (hasPaymentByPoints && payment.paymentByPoints[0].dynamicDailyPoints) {
        this._setDynamicRateChanges(rate, payment.paymentByPoints[0].dynamicDailyPoints);
      } else {
        rate.rateChanges.push(new RateChange(dailyPointsCost, this._model.stay.checkInDate, this._model.stay.checkOutDate));
      }
    }

    if (rateData.isRewardNight || rateData.freeNight) {
      taxes = ZERO;
    }

    rate.charges = new RateCharges(avgNightlyRate, price, taxes, total, serviceCharges, extraPersonCharge, extraPersonAndTaxes);
  }

  private _getApplicableFees(taxGroups, feeCode) {
    const fees = taxGroups.filter(taxGroup => feeCode.indexOf(taxGroup.code) !== -1 && taxGroup.isIncludedInRate === false)

    let fee = 0;
    for (let i = 0; i < fees.length; i++) {
      fee += Number(fees[i].amount)
    }
    return fee;
  }

  private _setRateCharges(rate: Rate, allRates: any, taxDefs = []) {
    const taxGroups = allRates && allRates.totalRate && allRates.totalRate.taxGroups && allRates.totalRate.taxGroups.length ? allRates.totalRate.taxGroups : [];

    let taxGroup;
    let taxDef;
    const descriptions = [];

    for (let i = 0; i < taxGroups.length; i++) {
      taxGroup = taxGroups[i];
      for (let j = 0; j < taxDefs.length; j++) {
        taxDef = taxDefs[j];
        if (taxGroup.code === taxDef.code && descriptions.indexOf(taxDef.description) === -1) {
          descriptions.push(taxDef.description)
          if (TAXCODES.indexOf(taxGroup.code) !== -1 && taxGroup.isIncludedInRate === false) {
            rate.taxCharges.push(new RateCharge(ChargeType.TAX, taxGroup.amount, taxDef.description, taxDef.formattedDescription, taxGroup.isIncludedInRate));
          }
          if (SERVICECODES.indexOf(taxGroup.code) !== -1 && taxGroup.isIncludedInRate === false) {
            rate.serviceCharges.push(new RateCharge(ChargeType.SERVICE, taxGroup.amount, taxDef.description, taxDef.formattedDescription, taxGroup.includedInPrice));
          }
        }
      }
    }
  }

  private _setRules(rate: Rate, rateData: any, policies: any) {
    const isAdvancePurchase = rateData && rateData.advanceBooking && rateData.advanceBooking.isAdvancePurchase ? rateData.advanceBooking.isAdvancePurchase : false;
    const advancePurchaseMinDays = rateData && rateData.advanceBooking && rateData.advanceBooking.formattedDescription ? rateData.advanceBooking.formattedDescription : false;
    if (isAdvancePurchase) {
      rate.rules.push(new RateRule(RateRuleType.ADVANCED_PURCHASE));
    }
    if (advancePurchaseMinDays) {
      rate.rules.push(new RateRule(RateRuleType.ADVANCED_BOOKING, advancePurchaseMinDays, advancePurchaseMinDays));
    }
    // combined with other china-specific logic in modifyRes component this restricts modification in certain instances
    rate.refundable = rateData.isRewardNight ? false : policies.isRefundable;
  }

  private _setDepositPolicy(rate: Rate, policies: any) {
    const deposit = policies && policies.deposit ? policies.deposit : {};
    // is there such a thing as an X Night Deposit where X > 1 and X < FullStay?
    // if there is we're not covering it here.

    if (deposit.formattedDescription || deposit.description) {
      rate.depositPolicy = new DepositPolicy(DepositPolicyType.DEFAULT_DEPOSIT, !!deposit.formattedDescription ? deposit.formattedDescription : deposit.description);
    } else if (deposit.feeType === 'FullStay') {
      rate.depositPolicy = new DepositPolicy(DepositPolicyType.FULL_DEPOSIT);
    } else if (deposit.feeType === 'Nights' && (deposit.feeValue === 1 || deposit.feeValue === '1')) {
      rate.depositPolicy = new DepositPolicy(DepositPolicyType.ONE_NIGHT_DEPOSIT);
    } else {
      rate.depositPolicy = null;
    }
  }

  private _setRateChanges(rate: Rate, allRates: any) {
    const dailyRates = allRates && allRates.dailyRates ? allRates.dailyRates : [];

    let dailyRate, fullRate;
    for (let i = 0; i < dailyRates.length; i++) {
      dailyRate = dailyRates[i];
      fullRate = dailyRate.dailyBaseOccupancyRate.baseAmount;
      rate.rateChanges.push(new RateChange(fullRate, DateUtils.fromISOToDate(dailyRate.start), DateUtils.fromISOToDate(dailyRate.end)));
    }
  }

  private _setDynamicRateChanges(rate: Rate, dynamicDailyPoints: any) {
    for (let i = 0; i < dynamicDailyPoints.length; i++) {
      rate.rateChanges.push(new RateChange(
        dynamicDailyPoints[i].dailyPointsCost,
        DateUtils.fromISOToDate(dynamicDailyPoints[i].start),
        DateUtils.fromISOToDate(DateUtils.addDaysTo(dynamicDailyPoints[i].start, 1))
      ));
    }
  }

  private _setOriginalRateChanges(rate: Rate, rateChanges: any) {
    for (const rateChange of rateChanges) {
      rate.originalRateChanges.push(new RateChange(rateChange.price, DateUtils.fromISOToDate(rateChange.startDate), DateUtils.fromISOToDate(rateChange.endDate)));
    }
  }

  private getSpecificProduct(products: Array<any>, productCode: String) {
    const singleProduct = products.filter(product => product.productCode === productCode)
    if (singleProduct.length) {
      return singleProduct[0];
    } else {
      return false;
    }
  }

  private getSpecificProductType(products: Array<any>, productTypeCode: String) {
    const singleProduct = products.filter(product => product.productTypeCode === productTypeCode)
    if (singleProduct.length) {
      return singleProduct[0];
    } else {
      return false;
    }
  }

  private _setRateName(ratePlan: any, rateCategory: any) {
    const defaultRateName = ratePlan && ratePlan.name ? ratePlan.name : '';
    const rateCategoryName = rateCategory && rateCategory.longName ? rateCategory.longName : '';
    return rateCategoryName ? rateCategoryName : defaultRateName;
  }

  private _setRateDescription(ratePlan: any, rateCategory: any) {
    const defaultRateDescription = ratePlan && ratePlan.description ? ratePlan.description : '';
    const rateCategoryDescription = rateCategory && rateCategory.longDescription ? rateCategory.longDescription : '';
    return rateCategoryDescription ? rateCategoryDescription : defaultRateDescription;
  }

  private _areSomeTaxesIncluded(offer) {
    let included = false;
    if (offer && offer.rates && offer.rates.totalRate && offer.rates.totalRate.taxGroups && offer.rates.totalRate.taxGroups.length) {

      const taxes = offer.rates.totalRate.taxGroups;
      for (let i = 0; i < taxes.length; i++) {
        if (TAXCODES.indexOf(taxes[i].code) !== -1) {
          if (taxes[i].isIncludedInRate) {
            included = true;
          }
        }
      }
    }
    return included;
  };
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/builders/reservationV2-model.builder.ts